/**
 * The stamp book (docs/POSTAGE_PLAN.md). Opened at `#/stamps`: the Express stamps a member holds,
 * one perforated sheet per correspondent, slots drawn up to the cap so the
 * book reads at a glance. 1st class is a pass, not a stamp, so it shows as a
 * badge. Stamps not tied to anyone (bought or gifted, later) get their own
 * sheet, shown only when there are some.
 *
 * Tapping a stamp readies Express on the next letter to that person; sealing
 * that letter is what spends it, server-side, exactly as before.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { POSTAGE_LABELS } from '@toiyp/shared';
import { useMyStampsQuery, useStampBookQuery, type MyStamps, type StampBookView } from '../letters/api';
import { useMineQuery, type RelationshipWithPeer } from '../relationships/api';
import { Avatar } from '../profiles/avatar';
import { useMeQuery } from '../profiles/api';
import { track } from '../onboarding/track';
import { FrankedStamp, PostageStamp, SpecialStamp, type SpecialStampKind } from './Stamp';
import { readyClass } from './intent';
import { spentSinceLastLook } from './bookMemory';
import { postageNotice } from './notice';
import { GiftBookLink } from './GiftStampBook';
import { collectionCaption } from './StampPicker';
import { bookContents } from './designs';
import { EnvelopeThumb } from '../stationery/Envelope';
import { arrivesPhrase } from '../letters/eta';
import { heldFor, heldPhrase } from './newcomer';

const SEEN_KEY = 'toiyp:stamp-book-seen';

type Peer = NonNullable<RelationshipWithPeer['peer']>;

function readSeen(): number {
  try {
    const v = localStorage.getItem(SEEN_KEY);
    return v ? new Date(v).getTime() : 0;
  } catch {
    return 0;
  }
}

function writeSeen(at: Date): void {
  try {
    localStorage.setItem(SEEN_KEY, at.toISOString());
  } catch {
    /* a private window: the animation just plays again next time */
  }
}

function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'long' });
}

function peerName(peer: Peer | null | undefined): string {
  return peer?.handle ? `@${peer.handle}` : (peer?.displayName ?? 'someone');
}

/** How many of each page's stamps arrived since the member last opened the book. */
export function arrivals(book: Pick<StampBookView, 'history'>, seenAt: number): Map<string, number> {
  const fresh = new Map<string, number>();
  for (const h of book.history) {
    if (h.kind !== 'allowance' || !h.relationshipId || new Date(h.at).getTime() <= seenAt) continue;
    fresh.set(h.relationshipId, (fresh.get(h.relationshipId) ?? 0) + h.delta);
  }
  return fresh;
}

export function StampBookScreen({ onBack }: { onBack: () => void }) {
  const { data: book, isLoading, error } = useStampBookQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: relationships } = useMineQuery();
  const { data: me } = useMeQuery();
  // Read once per visit, then move the mark — new stamps animate in exactly once.
  const [seenAt] = useState(readSeen);
  const marked = useRef(false);
  useEffect(() => {
    track({ event: 'stamp_book_viewed' });
  }, []);
  useEffect(() => {
    if (book && !marked.current) {
      marked.current = true;
      writeSeen(new Date());
    }
  }, [book]);

  const peers = useMemo(() => {
    const m = new Map<string, Peer | null>();
    for (const r of relationships ?? []) m.set(r.id, r.peer);
    return m;
  }, [relationships]);
  const fresh = useMemo(() => (book ? arrivals(book, seenAt) : new Map<string, number>()), [book, seenAt]);
  // Stamps spent since the book was last open leave a postmarked gap, once.
  const [gaps, setGaps] = useState<Map<string, number>>(new Map());
  const gapsLooked = useRef(false);
  useEffect(() => {
    if (!book || gapsLooked.current) return;
    gapsLooked.current = true;
    setGaps(new Map(book.pages.map((p) => [p.relationshipId, spentSinceLastLook(p.relationshipId, p.express)])));
  }, [book]);

  const readyExpress = (relationshipId: string) => {
    readyClass(relationshipId, 'express');
    postageNotice.show(`Express is ready on your next letter to ${peerName(peers.get(relationshipId))}.`);
    window.location.hash = `#/c/${relationshipId}`;
  };

  const pages = (book?.pages ?? []).filter((p) => peers.has(p.relationshipId));
  const anyCount = (book?.anyLetter.express ?? 0) + (book?.anyLetter.first ?? 0);

  return (
    <section className="screen" aria-label="Stamp book">
      <header className="screen__header">
        <button type="button" className="screen__back" onClick={onBack} aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <span className="screen__title">Stamp book</span>
      </header>

      <div className="stamp-book">
        <div className="stamp-book__cover" aria-hidden="true">
          <span className="stamp-book__cover-title">Stamp book</span>
          <span className="stamp-book__cover-owner">{me?.handle ? `@${me.handle}` : 'toiyp'}</span>
          <span className="stamp-book__cover-mark">letters, slowly</span>
        </div>

        {book && <SpecialEditions special={book.special} />}
        <StampDesigns />

        <div className="stamp-book__pages">
          {isLoading ? (
            <p className="stamp-book__empty">Opening your stamp book…</p>
          ) : error || !book ? (
            <p className="stamp-book__empty">The stamp book’s stuck in the drawer. Try again in a moment.</p>
          ) : !book.enabled ? (
            book.special.welcome.length + book.special.milestones.length > 0 ? null : (
              <p className="stamp-book__empty">The stamp counter isn’t open yet. Every letter goes 2nd class for now, and that’s fine.</p>
            )
          ) : pages.length === 0 && anyCount === 0 ? (
            <p className="stamp-book__empty">
              Nothing pressed between the pages yet. Your stamp book fills up as you and your correspondents keep writing.
            </p>
          ) : (
            <>
              {pages.map((p) => {
                const peer = peers.get(p.relationshipId) ?? null;
                const name = peerName(peer);
                const isNew = Math.min(fresh.get(p.relationshipId) ?? 0, p.express);
                const showSlots = p.express > 0 || p.expressEarned;
                const spent = gaps.get(p.relationshipId) ?? 0;
                const slots = Math.max(book.cap, p.express + spent);
                return (
                  <article key={p.relationshipId} className="stamp-sheet" aria-label={`Stamps for ${name}`}>
                    <header className="stamp-sheet__head">
                      <span className="stamp-sheet__avatar">
                        <Avatar name={peer?.displayName ?? peer?.handle ?? '?'} avatar={peer?.avatar ?? null} />
                      </span>
                      <span className="stamp-sheet__who">
                        <span className="stamp-sheet__name">{peer?.displayName ?? name}</span>
                        {peer?.handle && <span className="stamp-sheet__handle">@{peer.handle}</span>}
                      </span>
                      {p.firstClass && (
                        <span className="stamp-pass" title={`1st class is on for your letters to ${name}`}>
                          <PostageStamp cls="first" size="mini" />
                          1st class pass
                        </span>
                      )}
                    </header>

                    {showSlots && (
                      <>
                        <p className="stamp-sheet__label">
                          {POSTAGE_LABELS.express} stamps
                        </p>
                        <ol className="stamp-sheet__grid" aria-label={`${p.express} Express stamps for ${name}`}>
                          {Array.from({ length: slots }, (_, i) => {
                            const held = i < p.express;
                            // The newest stamps are the last held slots.
                            const arriving = held && i >= p.express - isNew;
                            if (!held && i < p.express + spent) {
                              // Spent since last time: franked, then fading to an empty slot.
                              return (
                                <li key={i} className="stamp-slot stamp-slot--spent" aria-hidden="true">
                                  <FrankedStamp cls="express" date={new Date()} size="medium" />
                                </li>
                              );
                            }
                            if (!held) {
                              return <li key={i} className="stamp-slot stamp-slot--empty" aria-hidden="true" />;
                            }
                            return (
                              <li
                                key={i}
                                className={`stamp-slot${arriving ? ' stamp-slot--new' : ''}`}
                                style={
                                  arriving
                                    ? ({ '--arrive-delay': `${(i - (p.express - isNew)) * 160}ms` } as React.CSSProperties)
                                    : undefined
                                }
                              >
                                <button
                                  type="button"
                                  className="stamp-slot__stamp"
                                  disabled={!p.expressEarned}
                                  onClick={() => readyExpress(p.relationshipId)}
                                  aria-label={
                                    p.expressEarned
                                      ? `Use an Express stamp on your next letter to ${name}`
                                      : `Express stamp for ${name}, paused`
                                  }
                                >
                                  <PostageStamp cls="express" size="sheet" />
                                </button>
                              </li>
                            );
                          })}
                        </ol>
                        <p className="stamp-sheet__note">
                          {p.expressEarned
                            ? `Next stamps arrive on ${dayMonth(book.nextAllowanceOn)}.`
                            : 'Paused for now — they come back when you’re writing regularly again.'}
                        </p>
                      </>
                    )}
                  </article>
                );
              })}

              {anyCount > 0 && (
                <article className="stamp-sheet stamp-sheet--any" aria-label="Stamps for anyone">
                  <header className="stamp-sheet__head">
                    <span className="stamp-sheet__who">
                      <span className="stamp-sheet__name">Stamps for anyone</span>
                      <span className="stamp-sheet__handle">Use them on any letter — tap the stamp on the letter you’re writing.</span>
                    </span>
                  </header>
                  <ol className="stamp-sheet__grid">
                    {Array.from({ length: book.anyLetter.express }, (_, i) => (
                      <li key={`e${i}`} className="stamp-slot">
                        <span className="stamp-slot__stamp">
                          <PostageStamp cls="express" size="sheet" />
                        </span>
                      </li>
                    ))}
                    {Array.from({ length: book.anyLetter.first }, (_, i) => (
                      <li key={`f${i}`} className="stamp-slot">
                        <span className="stamp-slot__stamp">
                          <PostageStamp cls="first" size="sheet" />
                        </span>
                      </li>
                    ))}
                  </ol>
                </article>
              )}

              {book.history.length > 0 && (
                <section className="stamp-book__history" aria-label="Stamp history">
                  <h2 className="stamp-book__history-title">Comings and goings</h2>
                  <ul>
                    {book.history.slice(0, 12).map((h, i) => (
                      <li key={`${h.at}-${i}`}>
                        <span>{historyLine(h, peerName(h.relationshipId ? peers.get(h.relationshipId) : null))}</span>
                        <time dateTime={h.at}>{new Date(h.at).toLocaleDateString([], { day: 'numeric', month: 'short' })}</time>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

export function historyLine(h: StampBookView['history'][number], name: string): string {
  const label = POSTAGE_LABELS[h.class];
  const n = Math.abs(h.delta);
  const stamps = `${n} ${label} stamp${n === 1 ? '' : 's'}`;
  switch (h.kind) {
    case 'allowance':
      return `${stamps} arrived for ${name}`;
    case 'spent':
      return `Used on a letter to ${name}`;
    case 'refund':
      return `Back in the book — the letter to ${name} was stopped`;
    default:
      return h.delta > 0 ? `${stamps} added` : `${stamps} used`;
  }
}

/** Where the stamp book lives in the URL — bookmarkable, survives a reload. */
export const STAMP_BOOK_HASH = '#/stamps';

export function openStampBook(): void {
  window.location.hash = STAMP_BOOK_HASH;
}

function counterpartName(c: StampBookView['special']['welcome'][number]['counterpart']): string {
  return c?.displayName?.trim() || (c?.handle ? `@${c.handle}` : 'a friend');
}

/**
 * Special editions (slice 4): welcome stamps and milestone designs. Kept on a
 * shelf of their own: they are keepsakes, not postage, and they show whether
 * or not the stamp counter is open.
 */
function SpecialEditions({ special }: { special: StampBookView['special'] }) {
  if (special.welcome.length + special.milestones.length === 0) return null;
  return (
    <section className="stamp-sheet special-editions" aria-label="Special editions">
      <header className="stamp-sheet__head">
        <span className="stamp-sheet__who">
          <span className="stamp-sheet__name">Special editions</span>
          <span className="stamp-sheet__handle">Keepsakes, not postage</span>
        </span>
      </header>
      <ul className="special-editions__grid">
        {special.milestones.map((m) => (
          <li key={`m-${m.milestone}`} className="special-editions__item">
            <SpecialStamp kind={`milestone-${m.milestone}` as SpecialStampKind} size="large" />
            <span className="special-editions__caption">{m.name}</span>
            <span className="special-editions__date">
              {m.milestone === 1 ? 'Your first person brought in' : `${m.milestone} people brought in`}
            </span>
          </li>
        ))}
        {special.welcome.map((w, i) => (
          <li key={`w-${i}`} className="special-editions__item">
            <SpecialStamp kind="welcome" size="large" />
            <span className="special-editions__caption">
              {w.kind === 'welcomed_by'
                ? `Welcomed by ${counterpartName(w.counterpart)}`
                : `You brought ${counterpartName(w.counterpart)}`}
            </span>
            <span className="special-editions__date">{dayMonth(w.at)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Custom stamps (toiyp Plus): the designs this member can print their stamps
 * with, one flat sheet per collection in an even grid. Plus collections they
 * cannot use yet are shown as they are, dimmed, with one gentle line — no
 * price and no button to buy. Plus members can give a stamp book from here.
 */
function StampDesigns() {
  const { data: stamps } = useMyStampsQuery(undefined, { refetchOnMountOrArgChange: true });
  if (!stamps || stamps.collections.length === 0) return null;
  return (
    <section className="stamp-sheet stamp-designs-book" aria-label="Stamp designs">
      <header className="stamp-sheet__head">
        <span className="stamp-sheet__who">
          <span className="stamp-sheet__name">Stamp designs &amp; paper</span>
          <span className="stamp-sheet__handle">Pick them on any letter. The class mark stays in the stamp’s corner.</span>
        </span>
        <GiftBookLink className="btn btn--ghost stamp-designs-book__gift" />
      </header>
      {stamps.collections.map((c) => (
        <div key={c.id} className={`stamp-designs-book__set${c.access === 'locked' ? ' is-locked' : ''}`}>
          <p className="stamp-sheet__label">
            {c.name}{' '}
            <span className="stamp-designs-book__from">
              · {collectionCaption(c)} · {bookContents(c)}
            </span>
          </p>
          {c.description && <p className="stamp-designs-book__desc">{c.description}</p>}
          {c.plusOnly && c.access !== 'locked' && (
            <GiftBookLink collectionId={c.id} label="Gift this book" className="linkish gift-link stamp-designs-book__gift-one" />
          )}
          {c.designs.length > 0 && (
            <ol className="stamp-designs-book__grid" aria-label={`${c.name}${c.access === 'locked' ? ', a preview' : ''}`}>
              {c.designs.map((d) => (
                <li key={d.id} className="stamp-designs-book__slot" title={d.name}>
                  <PostageStamp cls="second" size="large" design={d} decorative={false} />
                </li>
              ))}
            </ol>
          )}
          {c.papers.length > 0 && (
            <ol className="stamp-designs-book__papers" aria-label={`${c.name} letter paper`}>
              {c.papers.map((p) => (
                <li key={p.id} title={p.name}>
                  <span
                    className="stamp-designs-book__paper"
                    style={{ backgroundImage: `url(${p.imageUrl})`, aspectRatio: `${p.width} / ${p.height}` }}
                    role="img"
                    aria-label={`Letter paper: ${p.name}`}
                  />
                  <span className="stamp-designs-book__paper-name">{p.name}</span>
                </li>
              ))}
            </ol>
          )}
          {c.envelopes.length > 0 && (
            <ol className="stamp-designs-book__papers" aria-label={`${c.name} envelopes`}>
              {c.envelopes.map((e) => (
                <li key={e.id} title={e.name}>
                  <EnvelopeThumb envelope={e} title={`Envelope: ${e.name}`} />
                  <span className="stamp-designs-book__paper-name">{e.name}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
      <OutgoingGifts stamps={stamps} />
    </section>
  );
}

function OutgoingGifts({ stamps }: { stamps: MyStamps }) {
  const { data: relationships } = useMineQuery();
  if (stamps.outgoing.length === 0) return null;
  const nameOf = (id: string) => peerName(relationships?.find((r) => r.id === id)?.peer);
  return (
    <ul className="stamp-designs-book__outgoing" aria-label="Stamp books on their way">
      {stamps.outgoing.map((g) => (
        <li key={g.id}>
          {g.kind === 'newcomer' ? (
            <>{g.collectionName} for {heldFor(g)} — {heldPhrase(g)}</>
          ) : (
            <>
          {g.collectionName} for {nameOf(g.relationshipId ?? '')} —{' '}
          {g.kind === 'card' && g.deliverAt
            ? `in the post, arrives ${arrivesPhrase(g.deliverAt)}`
            : g.state === 'waiting'
              ? 'waiting for your next letter'
              : 'on its way with your letter'}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
